import { all, call, put, select, takeLatest } from 'redux-saga/effects';

import {
  selectLeadsBenchmarkSelectedDate,
  selectLeadsBenchmarkFirstDataRequestCompleted,
} from 'app/dealer/dashboard/Dashboard/LeadsBenchmark/redux/general/selectors';
import {
  leadsBenchmarkDataRequestBegin,
  leadsBenchmarkDataResetStatusRequest,
} from 'app/dealer/dashboard/Dashboard/LeadsBenchmark/redux/general/actions';
import {
  selectListingsBenchmarkSelectedDate,
  selectListingsBenchmarkFirstDataRequestCompleted,
} from 'app/dealer/dashboard/Dashboard/ListingsBenchmark/redux/general/selectors';
import {
  listingsBenchmarkDataRequestBegin,
  listingsBenchmarkDataResetStatusRequest,
} from 'app/dealer/dashboard/Dashboard/ListingsBenchmark/redux/general/actions';
import { DashboardActionTypes } from 'app/dealer/dashboard/constants';
import { selectBranches } from 'app/dealer/account/Branches/selectors';
import { selectNetworkId } from 'app/dealer/account/Network/selectors';
import { getBranches } from 'app/dealer/account/Branches/actions';
import { getNetwork } from 'app/dealer/account/Network/actions';
import { checkListingsTileCountupAnimationState } from 'app/dealer/dashboard/Dashboard/ListingsBenchmark/redux/general/sagas';

import { getDateRangeAsync } from '../dateRange/sagas';
import {
  selectDateRangeByFilteredLevel,
  selectFirstDateInDateRangeByFilteredLevel,
} from '../dateRange/selectors';
import { setInitialLevelAccordingToAuthority } from '../level/sagas';

// the date range can change now between requests
// i.e. every level filter has its own date range of available data,
//      so filtering for a network or a specific dealer will give us
//      an updated date range
//
//
// when we receive an updated date range:
// if the new start date is more recent than the currently selected date,
// then update the selected date to the new start date
// else update the selectedDate according to the payload in the argument
//
// this makes sure we don't allow the date picker to stay on a date that is no longer in range
export function prepareDateDataPayload(startDate, currentlySelectedDate) {
  const payload = {};
  // if start date is more recent than the current date,
  // we should prepare a payload to update the currentlySelectedDate with the start date
  if (new Date(startDate) > new Date(currentlySelectedDate)) {
    payload.data = new Date(startDate);
  }
  // else pass the currently selected date to the data request as normal
  else {
    payload.data = new Date(currentlySelectedDate);
  }
  return payload;
}

export function* getPrequisiteData() {
  const networkId = yield select(selectNetworkId);
  const branches = yield select(selectBranches);
  const currentDateRange = yield select(selectDateRangeByFilteredLevel);
  // if there isn't a network id, get the network
  if (!networkId || networkId?.length === 0) {
    yield put(getNetwork());
  }
  // if no branches exist, fetch them
  if (!branches || branches?.length === 0) {
    yield put(getBranches());
  }
  // fetch if it doesn't exist
  if (!currentDateRange) {
    yield call(getDateRangeAsync);
  }
}
export function* resetDataRequestStatuses() {
  if (yield select(selectLeadsBenchmarkFirstDataRequestCompleted)) {
    yield put(leadsBenchmarkDataResetStatusRequest());
  }

  if (yield select(selectListingsBenchmarkFirstDataRequestCompleted)) {
    yield put(listingsBenchmarkDataResetStatusRequest());
  }
}

/**
 * @function triggerAllDataRequestsAsync
 * @returns {Generator<any, void, unknown>}
 *
 * @description
 * generator is invoked whenever we need to get a complete data request
 * e.g. initial page comnponent mount
 *
 */

export function* triggerAllDataRequestsAsync() {
  // check to see if we need to disable the countup animation
  // countup animation only happens once i.e. the first time the user sees the initial insights
  yield call(checkListingsTileCountupAnimationState);

  // reset first data request statuses
  // this will help to make sure the loading status flow works when
  // new top level data is requested
  yield call(resetDataRequestStatuses);

  // get necessary top level data
  yield call(getPrequisiteData);

  const startDate = yield select(selectFirstDateInDateRangeByFilteredLevel);
  // setup calls
  const dataRequests = [
    {
      currentlySelectedDate: yield select(selectLeadsBenchmarkSelectedDate),
      action: leadsBenchmarkDataRequestBegin,
    },
    {
      currentlySelectedDate: yield select(selectListingsBenchmarkSelectedDate),
      action: listingsBenchmarkDataRequestBegin,
    },
  ].map(({ currentlySelectedDate, action }) => ({
    action,
    payload: prepareDateDataPayload(startDate, currentlySelectedDate),
  }));

  yield all(dataRequests.map(({ action, payload }) => put(action(payload))));
}

// when dashboard mounts, we set the initial filter level
// accessible to the user's authority
// then we trigger a data request
export function* handleDashboardMountedAsync() {
  yield call(setInitialLevelAccordingToAuthority);
  yield call(triggerAllDataRequestsAsync);
}

export function* watchTriggerAllDataRequests() {
  yield takeLatest(
    DashboardActionTypes.SET_DASHBOARD_MOUNTED,
    handleDashboardMountedAsync,
  );
}

export function* watchGeneralSagas() {
  yield all([call(watchTriggerAllDataRequests)]);
}
